import type { CoachSocialLinks, SocialLink } from "@/lib/schema";

export const SOCIAL_KEY_TO_PLATFORM: Record<
  keyof CoachSocialLinks,
  SocialLink["platform"]
> = {
  instagram: "Instagram",
  linkedin: "LinkedIn",
  twitter: "Twitter",
  youtube: "YouTube",
  tiktok: "TikTok",
  website: "Website",
  store: "Store",
  bookingCalendar: "BookingCalendar",
  retreatPage: "RetreatPage",
};

// Prepend https:// when the user/admin entered a bare value (e.g.
// "instagram.com/handle") so the rendered <a href> doesn't get treated as
// a relative URL. Preserves any existing scheme (http:, https:, mailto:, …).
export function ensureUrlScheme(url: string | undefined | null): string {
  if (!url) return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+\-.]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

// Always sends all 9 platforms: "" tells the server to delete that platform's
// stored link, so removing one link (or all of them) actually sticks.
export function socialLinksToArray(links: CoachSocialLinks): SocialLink[] {
  return (
    Object.entries(SOCIAL_KEY_TO_PLATFORM) as [
      keyof CoachSocialLinks,
      SocialLink["platform"],
    ][]
  ).map(([key, platform]) => ({
    platform,
    url: ensureUrlScheme(links[key]),
  }));
}

// href for a stored link, or undefined unless it is http(s). ensureUrlScheme
// keeps any scheme (javascript:, data:, mailto:), so its output must never go
// into an href directly. An <a> with no href renders as plain text.
export function safeHref(url: string | undefined | null): string | undefined {
  const withScheme = ensureUrlScheme(url);
  return /^https?:\/\//i.test(withScheme) ? withScheme : undefined;
}
